// Global Instructions Rule Applied!

import { FieldTypeEnum, SelectTypeEnum, SelectFieldConfig } from '../../../core/components/InputManager/types.ts'
import type { CountryChangePatch } from './types.ts'
import { ContactRecordFields } from '../../../state_manager/data_models/contact_record.ts'

/**
 * Maps ContactsTable column definitions to InputManager field configurations
 * Handles dynamic field type detection and option mapping
 */

/**
 * Maps a single column to InputManager field configuration
 */
export function mapColumnToInputManagerField(column: any, record: any, helpers: any, fieldType: FieldTypeEnum) {
  const { getLookupOptions } = helpers

  const columnKey = column.dataIndex || column.key
  const columnTitle = column.title
  const isRequired = column.required || false
  const isEditable = column.editable !== false

  const getCurrentValue = () => {
    // For city field, construct city object from code and label if available
    if (columnKey === ContactRecordFields.contacts__address_city_label) {
      const cityCode = record[ContactRecordFields.contacts__address_city_code]
      const cityLabel = record[ContactRecordFields.contacts__address_city_label]
      const countryCode = record[ContactRecordFields.contacts__address_country_code]
      const state = record[ContactRecordFields.contacts__address_stateprovince]

      // If we have a city code and label, construct the city object
      if (cityCode && cityLabel) {
        // Check if value is already an object
        if (typeof cityLabel === 'object' && cityLabel !== null) {
          return cityLabel
        }
        // Construct city object from code and label
        return {
          value: cityCode,
          name: cityLabel,
          label: cityLabel,
          data: { countryCode: countryCode || null, state: state || null }
        }
      }
      // Return existing value (could be object or string)
      return cityLabel || ''
    }

    // For country field, construct country object from code and label if available
    if (columnKey === ContactRecordFields.contacts__address_country_label) {
      const countryCode = record[ContactRecordFields.contacts__address_country_code]
      const countryLabel = record[ContactRecordFields.contacts__address_country_label]

      // If we have a country code and label, construct the country object
      if (countryCode && countryLabel) {
        // Check if value is already an object
        if (typeof countryLabel === 'object' && countryLabel !== null) {
          return countryLabel
        }
        // Construct country object from code and label
        return {
          value: countryCode,
          label: countryLabel
        }
      }
      // Return existing value (could be object or string)
      return countryLabel || ''
    }

    return record[columnKey] || ''
  }

  const selectConfig = (): Pick<SelectFieldConfig, 'selectType' | 'options'> => {
    return {
      selectType: undefined,
      options: []
    }
  }

  const baseField = {
    key: columnKey,
    label: columnTitle,
    type: fieldType,
    value: getCurrentValue(),
    required: isRequired,
    editable: isEditable,
    placeholder: `Enter ${columnTitle.toLowerCase()}...`
  }

  const selectConf = selectConfig()

  if (fieldType === FieldTypeEnum.SELECT) {
    return {
      ...baseField,
      selectType: selectConf.selectType,
      options: selectConf.options || [],
      allowClear: true
    }
  }

  if (fieldType === FieldTypeEnum.EMAIL) {
    return {
      ...baseField,
      type: FieldTypeEnum.EMAIL
    }
  }

  if (fieldType === FieldTypeEnum.PHONE) {
    return {
      ...baseField,
      type: FieldTypeEnum.PHONE
    }
  }

  if (fieldType === FieldTypeEnum.URL) {
    return {
      ...baseField,
      type: FieldTypeEnum.URL
    }
  }

  if (fieldType === FieldTypeEnum.CITY) {
    return {
      ...baseField,
      type: FieldTypeEnum.CITY,
      country: record[ContactRecordFields.contacts__address_country_code] || '',
      patchFields: [
        { sourceKey: 'cityCode', targetField: 'address_city_code' },
        { sourceKey: 'cityName', targetField: 'address_city_label' },
        { sourceKey: 'countryCode', targetField: 'address_country_code' },
        { sourceKey: 'state', targetField: 'address_stateprovince' }
      ],
      patchEndpoint: 'contacts',
      // Transform city object into API fields + Redux fields
      valueTransformer: (cityObject: any) => {
        // Handle clearing: when cityObject is empty string/null/undefined, set values to null explicitly
        const isClearing = !cityObject || cityObject === '' || (typeof cityObject === 'object' && !cityObject.value)

        // Extract values from CitySelect object: { label, value, name, countryCode, country, state }
        // Explicitly set to null when clearing (not undefined) so it's included in JSON
        const cityCode = isClearing ? null : (cityObject?.value ?? null)
        const cityLabel = isClearing ? null : cityObject?.name || cityObject?.label || null

        // Only include countryCode when selecting a city (not when clearing)
        // When clearing city, preserve the existing country
        const countryCode = isClearing ? undefined : (cityObject?.data?.countryCode ?? cityObject?.countryCode ?? undefined)
        const state = isClearing ? null : (cityObject?.data?.state ?? cityObject?.state ?? null)

        const result: Record<string, any> = {
          // API fields (mapped by patchFields)
          cityCode: cityCode,
          cityName: cityLabel,
          state: state,
          // Redux fields (direct mapping)
          [ContactRecordFields.contacts__address_city_label]: cityLabel,
          [ContactRecordFields.contacts__address_stateprovince]: state
        }

        // Only include countryCode if we're selecting a city (not clearing)
        if (!isClearing && countryCode !== undefined) {
          result.countryCode = countryCode
          result[ContactRecordFields.contacts__address_country_code] = countryCode
        }

        return result
      }
    }
  }

  if (fieldType === FieldTypeEnum.COUNTRY) {
    return {
      ...baseField,
      type: FieldTypeEnum.COUNTRY,
      patchFields: [
        { sourceKey: 'countryCode', targetField: 'address_country_code' },
        { sourceKey: 'cityCode', targetField: 'address_city_code' },
        { sourceKey: 'state', targetField: 'address_stateprovince' }
      ],
      patchEndpoint: 'contacts',
      // Transform country value and clear city and state
      valueTransformer: (countryObject: any): CountryChangePatch => {
        // Handle clearing: when countryObject is empty string/null/undefined, set countryCode to null explicitly
        const isClearing =
          !countryObject || countryObject === '' || (typeof countryObject === 'object' && !countryObject.value)
        const countryCode = isClearing ? null : (countryObject?.value ?? null)
        const countryLabel = isClearing ? null : (countryObject?.label ?? null)

        return {
          // API fields (mapped by patchFields)
          // Explicitly set to null when clearing (not undefined) so it's included in JSON
          countryCode: countryCode,
          cityCode: null, // Clear city when country changes
          state: null, // Clear state when country changes
          // Redux fields (direct mapping) - include both code and label for state updates
          [ContactRecordFields.contacts__address_country_code]: countryCode,
          [ContactRecordFields.contacts__address_country_label]: countryLabel,
          [ContactRecordFields.contacts__address_city_label]: null,
          [ContactRecordFields.contacts__address_stateprovince]: null
        }
      }
    }
  }

  return baseField
}

/**
 * Maps validation errors from API response to field-level errors
 */
export function mapValidationErrors(cellEditErrors: Record<string, string>, recordId: string): Record<string, string> {
  if (!cellEditErrors || !recordId) {
    return {}
  }

  const recordErrors = cellEditErrors[recordId]
  if (!recordErrors) {
    return {}
  }

  if (typeof recordErrors === 'string') {
    return { _general: recordErrors }
  }

  return recordErrors
}

/**
 * Handles auto-population logic for related fields
 */
export function handleAutoPopulation(
  fieldKey: string,
  newValue: any,
  record: any,
  onFieldChange: (key: string, value: any) => void,
  helpers: any
) {
  // City selection auto-populates country and state
  if (
    fieldKey === ContactRecordFields.contacts__address_city_label ||
    fieldKey === ContactRecordFields.contacts__address_city_code
  ) {
    if (newValue) {
      const countryCode = newValue?.data?.countryCode || newValue?.countryCode
      const cityLabel = newValue?.name || newValue?.label || ''
      const state = newValue?.data?.state ?? newValue?.state ?? null

      if (countryCode) {
        onFieldChange(ContactRecordFields.contacts__address_country_code, countryCode)
      }

      if (state !== undefined && state !== null) {
        onFieldChange(ContactRecordFields.contacts__address_stateprovince, state)
      }

      if (cityLabel && fieldKey === ContactRecordFields.contacts__address_city_label) {
        onFieldChange(ContactRecordFields.contacts__address_city_label, cityLabel)
      }
    }
  }

  // Country change clears city and state
  if (
    fieldKey === ContactRecordFields.contacts__address_country_code ||
    fieldKey === ContactRecordFields.contacts__address_country_label
  ) {
    if (newValue) {
      const countryCode = newValue?.value || newValue
      const countryLabel = newValue?.label || newValue

      if (countryCode) {
        onFieldChange(ContactRecordFields.contacts__address_country_code, countryCode)
        onFieldChange(ContactRecordFields.contacts__address_city_code, null)
        onFieldChange(ContactRecordFields.contacts__address_city_label, null)
        onFieldChange(ContactRecordFields.contacts__address_stateprovince, null)
      }

      if (countryLabel && countryLabel !== countryCode) {
        onFieldChange(ContactRecordFields.contacts__address_country_label, countryLabel)
      }
    }
  }
}
